<template>
  <div class="background">
    <div v-if="stripeIsLoaded" class="container">
      <head-title class="head-title">Credit card</head-title>
      <div class="subscription-management">

          <div class="subscription-management__card subscription-management__card_form card">
            <template v-if="isTariffMode && tariffPendingPayment">
              <div class="card__title">Amount to pay</div>
              <div class="card__tariff">
                {{tariffPendingPayment.cost | formatMoney}} / Month — “{{tariffPendingPayment.name}}”
              </div>
            </template>
            <template v-else>
              <div class="card__unlink-title">
                Enter your card<br/> to link it for payments
              </div>
            </template>
            <div class="form-group">
              <label for="card-element" class="label">
                Card
              </label>
              <div id="card-errors" role="alert"></div>
              <div id="card-element"></div>
            </div>
          </div>
          <div class="card__gray-layout"></div>

          <div class="subscription-management__notice subscription-management__notice_comission">Your bank may take an additional commission.</div>
          <div class="subscription-management__notice subscription-management__notice_email">The receipt will be sent to the linked email.</div>
          <red-label class="red-label" v-if="paymentError">
            Oops! Something went wrong!<br/> The bank declined your money transfer.
          </red-label>
          <button-el :disabled="sending" @click="submitPayment" left red large class="btn">
            <template v-if="isLinkCardMode">Link</template>
            <template v-else-if="isTariffMode">Pay</template>
            <icon-base red>
              <icon-check/>
            </icon-base>
          </button-el>
          <router-link class="btn-link" :to="backBtnUrl">
            <round-btn
              small
              :disabled="sending"
              class="subscription-management__icon-close"
            >
              <icon-base red>
                <icon-cross/>
              </icon-base>
            </round-btn>
          </router-link>
      </div>
    </div>
    <div class="spinner-container" v-else>
      <spinner-el class="spinner" />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatMoney } from 'src/utils/helpers';
import loadingScripts from 'src/mixins/loadingScripts';
import { prevUrlLocalStorageName, stripeKey } from 'src/utils/config';
import { IconCheck, IconCross } from 'Icons';
import PaymentModes from 'src/models/PaymentModes';

export default {
  mixins: [loadingScripts],
  components: {
    IconCross,
    IconCheck,
  },
  props: {
    tariffPlanID: Number,
    mode: {
      type: String,
      validator(value) {
        return value === PaymentModes.tariff || value === PaymentModes.linkCard;
      },
    },
  },
  data: () => ({
    backBtnUrl: '/subscription',
    sending: false,
    stripeIsLoaded: false,
    paymentError: false,
  }),
  mounted() {
    const prevUrl = localStorage.getItem(prevUrlLocalStorageName);
    this.backBtnUrl = prevUrl || this.backBtnUrl;
    const errorStripeLoadingText = 'Error loading a payment widget, please, try reloading the page or contact support.';
    const stripeUrl = 'https://js.stripe.com/v3/';
    this.tryToLoadScript(stripeUrl, errorStripeLoadingText).then(() => {
      this.stripeIsLoaded = true;
      this.$nextTick(this.initStripeControl);
    });
  },
  methods: {
    ...mapActions('subscription', ['setTariffPendingPayment']),
    initStripeControl() {
      this.checkTariffData();
      // Create a Stripe client.
      this.stripe = window.Stripe(stripeKey);

      // Create an instance of Elements.
      this.elements = this.stripe.elements({
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Montserrat:300,400,500,600,700,800',
          },
        ],
      });
      this.card = this.elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            // iconColor: '#c4f0ff',
            color: '#000',
            fontWeight: 300,
            fontFamily: 'Montserrat',
            fontSize: '14px',
            // ':-webkit-autofill': {
            //   color: '#fce883',
            // },
            textTransform: 'uppercase',
            '::placeholder': {
              color: '#B2B2B2',
            },
          },
          invalid: {
            iconColor: '#000',
            color: '#000',
            fontSize: '14px',
          },
        },
      });
      this.card.mount('#card-element');
    },
    checkTariffData() {
      if (!this.tariffPendingPayment && this.isTariffMode) {
        this.$router.replace(this.backBtnUrl);
      }
    },
    submitPayment() {
      this.sending = true;
      this.stripe.createToken(this.card).then((result) => {
        const errorElement = document.getElementById('card-errors');
        // Clear error message
        errorElement.textContent = '';
        if (result.error) {
          // Inform the customer that there was an error.
          errorElement.textContent = result.error.message;
          this.sending = false;
        } else if (this.isTariffMode) {
          this.paymentError = false;
          this.$apiService.updatePaymentCard(result.token.id).then(() => {
            this.$apiService.changeTariffPlan(this.tariffPendingPayment.id).then(() => {
              this.setTariffPendingPayment(null);
              this.$router.replace('/subscription');
            }).catch(() => {
              this.paymentError = true;
              this.sending = false;
            });
          });
        } else if (this.isLinkCardMode) {
          this.paymentError = false;
          this.$apiService.updatePaymentCard(result.token.id).then(() => {
            this.setTariffPendingPayment(null);
            this.$router.replace('/subscription');
          }).catch(() => {
            this.paymentError = true;
            this.sending = false;
          });
        }
      }).catch(() => {
        this.sending = false;
      });
    },
  },
  filters: {
    formatMoney,
  },
  computed: {
    ...mapState('subscription', ['tariffPendingPayment']),
    isTariffMode() {
      return this.mode === PaymentModes.tariff;
    },
    isLinkCardMode() {
      return this.mode === PaymentModes.linkCard;
    },
  },
};
</script>


<style lang="scss" scoped>
  .background {
    @include background;
  }
  $container-width: 497px;
  .container {
    @include container;
    background: $white-color2;
    border-radius: 5px;
    max-width: $container-width;
  }
  .head-title {
    padding: 35px 29px 32px;
    border-bottom: 1px solid $white-color5;
  }
  .subscription-management {
    padding: 40px 29px 30px;
    position: relative;
    &__card {
      position: relative;
      width: 428px;
      background: $white-color2;
      border-radius: 10px;
      border: 1px solid $grey-color12;
      padding: 55px 29px 50px 30px;
      &_form {
        z-index: 2;
        height: 258px;
      }
    }
    &__notice {
      @include font(13px, $grey-color2);
      letter-spacing: -0.6px;
      &_comission {
        margin-top: 49px;
        margin-bottom: 17px;
      }
      &_email {
        margin-bottom: 59px;
      }
    }
    .card__gray-layout {
      border-radius: 10px;
      background: $grey-color6;
      width: 428px;
      height: 258px;
      position: absolute;
      z-index: 1;
      top: 49px;
      right: 30px;
    }
    &__icon-close {
      cursor: pointer;
      display: inline-flex;
    }
  }
  .btn {
    margin-right: 27px;
    padding: 10px 13px 11px 18px;
  }
  .btn-link {
    text-decoration: none;
  }

  .card {
    &__title {
      @include font(12px, $grey-color2, 500);
      margin-top: 5px;
    }
    &__tariff {
      margin-top: 8px;
      @include font(17px, $black-color1, 500);
      line-height: 27px;
      margin-bottom: 42px;
    }
    &__holder {
      display: inline-block;
      width: 100%;
      vertical-align: top;
      max-width: 264px;
      margin-right: 25px;
    }
    &__cvc {
      max-width: 68px;
      float: right;
      margin-top: 148px;
    }
    &__unlink-title {
      @include font(17px, $grey-color2, 500);
      line-height: 28px;
    }
  }
  .label {
    @include label;
    margin-bottom: 13px;
  }
  .form-group {
    margin-top: 39px;
    position: relative;
  }
  .spinner {
    margin: auto;
  }
  .red-label {
    margin-top: -31px;
    padding: 25px 30px 27px;
    line-height: 20px;
    margin-bottom: 61px;
  }
  #card-errors {
    position: absolute;
    right: 0;
    top: 0;
    @include font(13px, $red-color3, 500);
  }
  $side-padding: 10px;
  $bp: $container-width + ($side-padding * 2) - 1;
  @media screen and (max-width: 497px) {
    .background {
      padding: 10px $side-padding 45px;
    }
    .subscription-management {
      padding: 40px 0px 30px;
      margin-right: 10px;
      .card__gray-layout{
        width: 100%;
        right: -10px;
      }
      &__notice {
        font-size: 11px;
        letter-spacing: -0.5px;
      }
      &__notice_comission {
        margin: 49px 0 14px 30px;
      }
      &__notice_email {
        margin: 0 0 66px 30px;
      }
      &__card {
        width: 100%;
        padding: 55px 8px 4px 10px;
      }
    }
    .card__unlink-title {
      padding-left: 18px;
    }
    .form-group {
      margin-top: 39px;
      .label {
        padding-left: 19px;
      }
    }
    .red-label {
      @include incrSelectorWeight(1) {
        padding: 25px 18px 29px;
        margin-bottom: 60px;
        margin-left: 15px;
      }
    }
    .btn {
      margin-left: 29px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/subscription/Payment.vue